import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import storage from '../storage/storage';

export type Theme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'theme';
export const PRIME_THEME_LINK_ID = 'prime-theme';
export const PRIME_THEME_HREF: Record<Theme, string> = { light: 'themes/light/theme.css', dark: 'themes/dark/theme.css' };
export const THEME_PAGE_BACKGROUND: Record<Theme, string> = { light: '#f9fafb', dark: '#0f0f0f' };
export const SYSTEM_DARK_QUERY = '(prefers-color-scheme: dark)';

const PENDING_ATTRIBUTE = 'data-prime-theme-pending';
/** The PrimeReact theme file that is loaded but not applied (`media="not all"`), ready for an instant switch. */
const STANDBY_ATTRIBUTE = 'data-prime-theme-standby';
/** On `<html>` while a theme switch is painted: root.scss turns off every transition, so nothing fades on its own clock. */
const THEME_SWITCHING_ATTRIBUTE = 'data-theme-switching';
const NOT_APPLIED = 'not all';

/** Only the exact strings `light` and `dark` are a theme; anything else counts as "nothing stored". */
export const parseTheme = (value: unknown): Theme | null => (value === 'light' || value === 'dark' ? value : null);

export const getSystemTheme = (): Theme =>
  typeof window.matchMedia === 'function' && window.matchMedia(SYSTEM_DARK_QUERY).matches ? 'dark' : 'light';

/** Calls `onChange` when the system theme changes. Returns the function that stops it. */
export const watchSystemTheme = (onChange: (theme: Theme) => void): (() => void) => {
  if (typeof window.matchMedia !== 'function') return () => undefined;
  const query = window.matchMedia(SYSTEM_DARK_QUERY);
  const listener = (event: { matches: boolean }): void => onChange(event.matches ? 'dark' : 'light');
  query.addEventListener('change', listener);
  return () => query.removeEventListener('change', listener);
};

export const readStoredTheme = async (): Promise<Theme | null> => {
  try {
    return parseTheme(await storage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
};

export const storeTheme = async (theme: Theme): Promise<void> => {
  if (Capacitor.isNativePlatform()) {
    // The first-paint script in index.html reads only localStorage, while storage keeps the native theme in Preferences.
    try {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {
      // The first paint falls back to the system theme.
    }
  }
  try {
    await storage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // The theme still applies for this session.
  }
};

const createPrimeLink = (theme: Theme): HTMLLinkElement => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = PRIME_THEME_HREF[theme];
  return link;
};

const OTHER_THEME: Record<Theme, Theme> = { light: 'dark', dark: 'light' };

/** The links whose sheet has loaded. The applied #prime-theme counts as loaded once it stops being the current one. */
const loadedLinks = new WeakSet<HTMLLinkElement>();

/**
 * Turns off every CSS transition until two frames are painted, so the colors of the new theme show at once everywhere,
 * instead of each component fading on its own clock (0.1 to 0.3 s) while the others change at once.
 */
const suppressTransitionsForSwitch = (): void => {
  const root = document.documentElement;
  root.setAttribute(THEME_SWITCHING_ATTRIBUTE, '');
  window.requestAnimationFrame(() => window.requestAnimationFrame(() => root.removeAttribute(THEME_SWITCHING_ATTRIBUTE)));
};

/**
 * Applies a loaded sheet or takes it off through its CSSOM media list. Chrome applies this in the same task; a change of
 * the `media` attribute instead makes Chrome process the file again and apply it some frames later.
 */
const setApplied = (link: HTMLLinkElement, applied: boolean): void => {
  if (link.sheet) link.sheet.media.mediaText = applied ? '' : NOT_APPLIED;
};

/** Makes `next` the applied PrimeReact theme and keeps `current` loaded, not applied, for the switch back. */
const promote = (current: HTMLLinkElement, next: HTMLLinkElement): void => {
  setApplied(next, true);
  setApplied(current, false);
  loadedLinks.add(current);
  current.removeAttribute('id');
  current.setAttribute(STANDBY_ATTRIBUTE, '');
  next.removeAttribute(STANDBY_ATTRIBUTE);
  next.removeAttribute(PENDING_ATTRIBUTE);
  next.id = PRIME_THEME_LINK_ID;
};

/**
 * Fetches the file of `theme` in the background, right after `current`. The `media="not all"` attribute keeps it from
 * applying (and gives it the lowest fetch priority); from then on only its CSSOM media list changes (`setApplied`).
 */
const addStandby = (current: HTMLLinkElement, theme: Theme): void => {
  const standby = createPrimeLink(theme);
  standby.media = NOT_APPLIED;
  standby.setAttribute(STANDBY_ATTRIBUTE, '');
  standby.addEventListener('load', () => loadedLinks.add(standby));
  standby.addEventListener('error', () => standby.remove());
  current.after(standby);
};

/**
 * Applies the PrimeReact theme file of `theme`. Both files stay in the head, right where the first one was (never at
 * the end, so the app overrides keep coming after them): the applied one is #prime-theme, the other is loaded but not
 * applied. A switch to a loaded file only swaps the two media lists, in the same task as `data-theme`, so the
 * PrimeReact and the app colors change in the same frame. A file that has not loaded yet replaces the current one
 * when it loads: no unstyled page between.
 */
export const swapPrimeTheme = (theme: Theme): void => {
  document.head.querySelectorAll(`link[${PENDING_ATTRIBUTE}]`).forEach((pending) => pending.remove());

  const current = document.getElementById(PRIME_THEME_LINK_ID) as HTMLLinkElement | null;
  if (!current) {
    const link = createPrimeLink(theme);
    link.id = PRIME_THEME_LINK_ID;
    document.head.insertBefore(link, document.head.querySelector('style, link[rel="stylesheet"]'));
    addStandby(link, OTHER_THEME[theme]);
    return;
  }

  const standbys = Array.from(document.head.querySelectorAll<HTMLLinkElement>(`link[${STANDBY_ATTRIBUTE}]`));
  if (current.getAttribute('href') === PRIME_THEME_HREF[theme]) {
    if (!standbys.some((link) => link.getAttribute('href') === PRIME_THEME_HREF[OTHER_THEME[theme]])) {
      addStandby(current, OTHER_THEME[theme]);
    }
    return;
  }

  const ready = standbys.find((link) => link.getAttribute('href') === PRIME_THEME_HREF[theme] && loadedLinks.has(link));
  if (ready) {
    promote(current, ready);
    return;
  }

  const next = createPrimeLink(theme);
  next.setAttribute(PENDING_ATTRIBUTE, '');
  next.addEventListener('load', () => {
    if (!next.isConnected || !current.isConnected) return;
    standbys.forEach((link) => link.getAttribute('href') === PRIME_THEME_HREF[theme] && link.remove());
    suppressTransitionsForSwitch();
    promote(current, next);
  });
  next.addEventListener('error', () => next.remove());
  current.after(next);
};

/**
 * The status bar icons must contrast with the strip behind them. Android: the strip is the native background, which
 * follows the system night mode, so MainActivity sets the icons from the system and nothing is called here. iOS: the
 * web view runs under the status bar (`contentInset: 'always'`), so the strip is the page in the app theme and the icons
 * follow the app theme.
 */
export const syncStatusBar = async (theme: Theme): Promise<void> => {
  if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;
  try {
    await StatusBar.setStyle({ style: theme === 'dark' ? Style.Dark : Style.Light });
  } catch {
    // The icons keep their previous style.
  }
};

const setMeta = (name: string, content: string): void => {
  let meta = document.head.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

export const applyTheme = (theme: Theme): void => {
  const previous = document.documentElement.getAttribute('data-theme');
  if (previous !== null && previous !== theme) suppressTransitionsForSwitch();
  document.documentElement.setAttribute('data-theme', theme);
  setMeta('theme-color', THEME_PAGE_BACKGROUND[theme]);
  setMeta('color-scheme', theme);
  swapPrimeTheme(theme);
  void syncStatusBar(theme);
};
